/**
 * SitInListener — long-form meeting capture for Coast.
 *
 * Browser-side recorder that "sits in" on a 5-40 minute conversation.
 * Captures audio via MediaRecorder (in case we want to re-transcribe
 * server-side later), runs live speech recognition in parallel via the
 * Web Speech API, and POSTs the final transcript to /me/meeting-parse
 * for structured meeting intelligence.
 *
 * Browser limits we have to work around:
 *   • Web Speech API's SpeechRecognition session ends silently after a
 *     few minutes (or on long silences). We restart it automatically
 *     while recording is active so a 40-min capture works.
 *   • Browser tabs throttle background timers — keep the meeting tab
 *     visible during capture or warn the user.
 *   • MediaRecorder produces a blob; we don't upload it for v1, but
 *     we keep it in memory in case the user wants to download or we
 *     later route it to Whisper for higher-quality transcription.
 *
 * UX phases:
 *   idle      — record button visible, no audio capture yet
 *   recording — live transcript scrolls, timer counts up, pause/stop
 *   paused    — recording stopped, transcript preserved, can resume
 *   parsing   — final transcript sent to /me/meeting-parse, awaiting
 *               structured intel back
 *   results   — show structured intel + raw transcript, save/discard
 *   error     — something failed (permission denied, network, model)
 *
 * Speaker labels: Claude-guessed only (per user choice in Phase 1).
 * We surface this honestly in the UI — labels are tagged "AI-guessed".
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://coast-terminal-production.up.railway.app';
// Palette matches Dashboard.tsx (where users actually spend most of
// their time). Dashboard is light-themed: white background, deep-navy
// text, bronze-gold accent. CLAUDE.md mentions a darker scheme but
// every visible page in the deployed app uses these tokens.
const ACCENT      = '#b8956a';   // Dashboard's actual gold (not CLAUDE.md's #C9A96E)
const ACCENT_2    = '#9d7d56';
const BG          = '#FFFFFF';
const SURFACE     = '#FAFAF8';
const SURFACE_HI  = '#F5F0E8';
const BORDER      = '#E6DDD4';
const TEXT        = '#0D3D5C';
const TEXT_DIM    = '#6B7280';
const TEXT_FAINT  = '#9CA3AF';
const RED         = '#CC2200';
const GREEN       = '#1A7A4A';

interface Props {
  token:    string;
  onClose:  () => void;
  // Optional callback for "Save" — caller can persist the meeting somewhere
  // (Board card, dedicated meetings list, etc.). For v1 we just log.
  onSave?:  (data: MeetingResult) => void;
}

interface Speaker {
  id:         string;
  name?:      string | null;
  role_guess?: string | null;
  confidence: 'high' | 'medium' | 'low';
  share_pct:  number;
}
interface Topic       { label: string; summary: string; anchor: string; }
interface Decision    { decision: string; quote: string; }
interface ActionItem  { action: string; owner: string | null; deadline: string | null; quote: string; }
interface OpenQ       { question: string; quote: string; }
interface Risk        { risk: string; quote: string; }
interface MeetingEntities {
  addresses:   string[];
  properties:  string[];
  tribes:      string[];
  regulations: string[];
  people:      string[];
  money:       string[];
}
interface ParsedMeeting {
  summary:        string;
  speakers:       Speaker[];
  topics:         Topic[];
  decisions:      Decision[];
  action_items:   ActionItem[];
  open_questions: OpenQ[];
  risks_flagged:  Risk[];
  entities:       MeetingEntities;
}
export interface MeetingResult {
  transcript:    string;
  parsed:        ParsedMeeting | null;
  recorded_at:   string;
  duration_sec:  number;
}

// Minimal shape of the browser SpeechRecognition we use. Real type lives in
// the DOM lib but is browser-specific; we ambient-shim it.
type SR = {
  continuous:     boolean;
  interimResults: boolean;
  lang:           string;
  start:          () => void;
  stop:           () => void;
  abort:          () => void;
  onresult:       ((ev: any) => void) | null;
  onerror:        ((ev: any) => void) | null;
  onend:          (() => void) | null;
};

type Phase = 'idle' | 'recording' | 'paused' | 'parsing' | 'results' | 'error';

export default function SitInListener({ token, onClose, onSave }: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<string>('');
  const [interim, setInterim]       = useState<string>('');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [parsed, setParsed]         = useState<ParsedMeeting | null>(null);
  const [recordedAtIso, setRecordedAtIso] = useState<string | null>(null);

  const recognitionRef = useRef<SR | null>(null);
  const wantRunningRef = useRef(false);   // user intent — auto-restart while true
  const mediaRecRef    = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const transcriptRef  = useRef<string>('');
  const timerRef       = useRef<any>(null);
  const elapsedAtPauseRef = useRef<number>(0);
  const startTimeRef   = useRef<number>(0);

  // ── Waveform analyser refs + state (item 5: live audio bars) ────────
  // We tap the same MediaStream that MediaRecorder uses, run it through
  // an AnalyserNode at small fftSize for a 32-bin equaliser, and sample
  // its byte-frequency data inside requestAnimationFrame.
  const audioCtxRef     = useRef<AudioContext | null>(null);
  const analyserRef     = useRef<AnalyserNode | null>(null);
  const sourceNodeRef   = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef          = useRef<number | null>(null);
  const [bars, setBars] = useState<number[]>(new Array(32).fill(2));

  // ── Heartbeat tracker (item 3: warn if speech recognition silently dies)
  // Web Speech sessions can quietly stop emitting results after a long
  // run of silence or after the browser tab is partially backgrounded.
  // We track when the transcript last grew and, while recording is
  // active, show a warning if 60s pass with no growth despite the mic
  // still being live.
  const lastGrowthAtRef             = useRef<number>(Date.now());
  const [silenceWarning, setSilenceWarning] = useState(false);

  // Keep transcriptRef in sync so the SpeechRecognition callbacks (which
  // close over the ref, not the state) can append without staleness.
  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);

  // Browser feature detection
  const SR_CTOR: any = (window as any).SpeechRecognition
                    || (window as any).webkitSpeechRecognition;

  /* ── Audio capture ─────────────────────────────────────────────────── */
  const startAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      mr.start(1000);    // chunked every 1s so we never lose more than 1s on crash
      mediaRecRef.current = mr;

      // Set up the waveform analyser. Safari needs AudioContext to be
      // unlocked by a user gesture — getUserMedia counts.
      try {
        const AC: any = (window as any).AudioContext
                     || (window as any).webkitAudioContext;
        if (AC) {
          const ctx: AudioContext = new AC();
          const src   = ctx.createMediaStreamSource(stream);
          const anal  = ctx.createAnalyser();
          anal.fftSize = 64;          // 32 frequency bins
          anal.smoothingTimeConstant = 0.75;
          src.connect(anal);
          audioCtxRef.current = ctx;
          sourceNodeRef.current = src;
          analyserRef.current = anal;
          startWaveformLoop();
        }
      } catch { /* waveform is decorative — never block recording on it */ }
    } catch (e: any) {
      throw new Error(
        e?.name === 'NotAllowedError'
          ? 'Microphone permission denied. Allow microphone in Safari settings.'
          : `Could not start microphone: ${e?.message || e}`
      );
    }
  };

  const stopAudio = () => {
    try { mediaRecRef.current?.stop(); } catch {}
    try { audioStreamRef.current?.getTracks().forEach(t => t.stop()); } catch {}
    mediaRecRef.current = null;
    audioStreamRef.current = null;
    // Tear down the waveform analyser too.
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    try { sourceNodeRef.current?.disconnect(); } catch {}
    try { analyserRef.current?.disconnect(); } catch {}
    try { audioCtxRef.current?.close(); } catch {}
    sourceNodeRef.current = null;
    analyserRef.current = null;
    audioCtxRef.current = null;
    setBars(new Array(32).fill(2));
  };

  /* Sample analyser frequency data on requestAnimationFrame and push
   * a 32-bar array into state. The render is ~60fps but we let React
   * coalesce — each setBars batches to the next paint, so we're not
   * triggering a full reconciliation per frame. */
  const startWaveformLoop = () => {
    const buf = new Uint8Array(32);
    const tick = () => {
      const anal = analyserRef.current;
      if (!anal) return;
      anal.getByteFrequencyData(buf);
      // Normalise 0-255 → 2..40 pixel height. The minimum 2px keeps a
      // visible baseline when silent so the bars don't disappear.
      const next: number[] = new Array(32);
      for (let i = 0; i < 32; i++) {
        next[i] = 2 + Math.round((buf[i] / 255) * 38);
      }
      setBars(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  /* ── Speech recognition (with auto-restart) ───────────────────────── */
  const startRecognition = useCallback(() => {
    if (!SR_CTOR) return;
    const rec: SR = new SR_CTOR();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = 'en-US';

    rec.onresult = (ev: any) => {
      let interimPart = '';
      let appended = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const result = ev.results[i];
        const text = result[0]?.transcript ?? '';
        if (result.isFinal) {
          appended += text;
        } else {
          interimPart += text;
        }
      }
      if (appended) {
        const next = (transcriptRef.current
                      ? transcriptRef.current + (transcriptRef.current.endsWith(' ') ? '' : ' ')
                      : '') + appended.trim() + ' ';
        transcriptRef.current = next;
        setTranscript(next);
        // Heartbeat: transcript just grew. Reset the silence-warning
        // timer so we only flag warning after a sustained quiet period.
        lastGrowthAtRef.current = Date.now();
        if (silenceWarning) setSilenceWarning(false);
      } else if (interimPart) {
        // Interim text counts as growth too — Speech is processing.
        lastGrowthAtRef.current = Date.now();
        if (silenceWarning) setSilenceWarning(false);
      }
      setInterim(interimPart);
    };

    rec.onerror = (ev: any) => {
      // 'no-speech' is fine — the user just paused. Other errors get
      // surfaced but we still try to restart if the user wants us to.
      const code = ev?.error;
      if (code && code !== 'no-speech' && code !== 'aborted') {
        // eslint-disable-next-line no-console
        console.warn('SpeechRecognition error:', code);
      }
    };

    rec.onend = () => {
      // Web Speech API ends sessions on its own. As long as the user
      // hasn't paused/stopped, immediately restart so a 40-min meeting
      // actually transcribes end-to-end.
      if (wantRunningRef.current) {
        try { rec.start(); } catch { /* throw means already started — ignore */ }
      }
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch { /* dup start — ignore */ }
  }, [SR_CTOR]);

  const stopRecognition = () => {
    wantRunningRef.current = false;
    const rec = recognitionRef.current;
    if (rec) {
      try { rec.onend = null; rec.stop(); } catch {}
    }
    recognitionRef.current = null;
  };

  /* ── Top-level controls ────────────────────────────────────────────── */
  const beginRecording = async () => {
    setError(null);
    setTranscript('');
    setInterim('');
    setElapsedSec(0);
    elapsedAtPauseRef.current = 0;
    setParsed(null);
    transcriptRef.current = '';
    lastGrowthAtRef.current = Date.now();
    setSilenceWarning(false);
    if (!SR_CTOR) {
      setError('This browser does not support live speech recognition. Use Chrome or Safari on macOS.');
      setPhase('error');
      return;
    }
    try {
      await startAudio();
    } catch (e: any) {
      setError(e?.message || String(e));
      setPhase('error');
      return;
    }
    wantRunningRef.current = true;
    startRecognition();
    startTimeRef.current = Date.now();
    timerRef.current = window.setInterval(() => {
      setElapsedSec(
        Math.floor((Date.now() - startTimeRef.current) / 1000)
          + elapsedAtPauseRef.current,
      );
      // Heartbeat check (item 3): if no transcript growth in 60s while
      // recording, the Speech API has likely silently ended. Flag it
      // so the user knows to stop & retry instead of finding out after
      // 40 minutes that nothing was captured past minute 3.
      const sinceGrowth = Date.now() - lastGrowthAtRef.current;
      if (sinceGrowth > 60_000) setSilenceWarning(true);
    }, 1000);
    setRecordedAtIso(new Date().toISOString());
    setPhase('recording');
  };

  const pauseRecording = () => {
    stopRecognition();
    try { mediaRecRef.current?.pause(); } catch {}
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    elapsedAtPauseRef.current = elapsedSec;
    setPhase('paused');
  };

  const resumeRecording = () => {
    wantRunningRef.current = true;
    try { mediaRecRef.current?.resume(); } catch {}
    startRecognition();
    startTimeRef.current = Date.now();
    if (!timerRef.current) {
      timerRef.current = window.setInterval(() => {
        setElapsedSec(
          Math.floor((Date.now() - startTimeRef.current) / 1000)
            + elapsedAtPauseRef.current,
        );
      }, 1000);
    }
    setPhase('recording');
  };

  const stopAndParse = async () => {
    // The Web Speech API often has interim text in flight when we ask
    // it to stop. Calling rec.stop() doesn't always emit those interim
    // results as final — they can just get dropped. Two defences:
    //   1. Snapshot whatever's in the `interim` buffer right now.
    //   2. Wait ~350ms for any pending onresult to commit before we
    //      tear down the recognizer.
    const interimSnap = interim.trim();
    wantRunningRef.current = false;  // prevent auto-restart during the wait
    await new Promise(r => setTimeout(r, 350));
    stopRecognition();
    stopAudio();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setInterim('');

    // Merge: final transcript + any leftover interim that never got
    // finalised (e.g. user stopped mid-sentence).
    let finalTranscript = transcriptRef.current.trim();
    if (interimSnap && !finalTranscript.toLowerCase().endsWith(interimSnap.toLowerCase())) {
      finalTranscript = finalTranscript
        ? `${finalTranscript} ${interimSnap}`
        : interimSnap;
    }
    finalTranscript = finalTranscript.trim();

    if (!finalTranscript) {
      // Try to give a useful diagnostic. The most common causes:
      //   • Microphone not permitted (Safari/Chrome prompt)
      //   • Tab wasn't focused while speaking
      //   • Speech recognition session ended silently
      const hasMicTracks = !!audioStreamRef.current?.getAudioTracks().length;
      const recoveredAudio = audioChunksRef.current.length > 0;
      setError(
        "Didn't catch any speech.\n\n" +
        "Things to check:\n" +
        "• Microphone permission for this site\n" +
        "• The browser tab was focused while speaking\n" +
        "• You spoke loud enough to register\n\n" +
        `Diagnostic: mic ${hasMicTracks ? 'was' : 'was NOT'} streaming, ` +
        `${recoveredAudio ? audioChunksRef.current.length + ' audio chunks captured' : 'no audio chunks captured'}.`
      );
      setPhase('error');
      return;
    }
    setPhase('parsing');
    try {
      const r = await axios.post(`${API}/me/meeting-parse`, {
        transcript:       finalTranscript,
        duration_seconds: elapsedSec,
      }, {
        headers: { Authorization: `Bearer ${token}` },
        // 40-min meetings can be 6k+ tokens of input → Claude can take
        // 20-60s to generate the structured digest. Give it room.
        timeout: 180_000,
      });
      const data = r.data as { transcript: string; parsed: ParsedMeeting | null; parse_error?: string };
      if (!data.parsed) {
        setError(data.parse_error || 'Could not parse the meeting.');
        setPhase('error');
        return;
      }
      setParsed(data.parsed);
      setPhase('results');
    } catch (e: any) {
      const detail: string = e?.response?.data?.detail
                  || e?.response?.data?.message
                  || e?.message
                  || 'unknown error';
      // Detect Anthropic billing failures and surface a useful message
      // instead of the raw API dump. The Anthropic 400 includes the
      // literal phrase "credit balance is too low" — pattern-match it.
      const lowCredit = /credit balance is too low|insufficient_credit|billing/i
                          .test(detail);
      if (lowCredit) {
        setError(
          "Anthropic API credits are empty.\n\n" +
          "The backend's ANTHROPIC_API_KEY belongs to an account with no remaining credit. " +
          "Add credits at console.anthropic.com → Settings → Plans & Billing, " +
          "or rotate the key to a workspace that has credits.\n\n" +
          "Your recording wasn't lost — once credits are added, hit Try Again " +
          "and the same transcript will get parsed."
        );
      } else {
        setError(`Meeting parse failed: ${detail}`);
      }
      setPhase('error');
    }
  };

  // Cleanup on unmount — never leave a microphone hot.
  useEffect(() => {
    return () => {
      stopRecognition();
      stopAudio();
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fmtElapsed = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0
      ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
      : `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSave = () => {
    if (!parsed) return;
    const result: MeetingResult = {
      transcript:   transcriptRef.current.trim(),
      parsed,
      recorded_at:  recordedAtIso || new Date().toISOString(),
      duration_sec: elapsedSec,
    };
    if (onSave) onSave(result);
    onClose();
  };

  const handleDiscard = () => {
    if (phase === 'recording' || phase === 'paused') {
      const ok = window.confirm('Discard the recording so far?');
      if (!ok) return;
    }
    stopRecognition();
    stopAudio();
    if (timerRef.current) clearInterval(timerRef.current);
    onClose();
  };

  /* ── Render ────────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9000,
        background: 'rgba(0,0,0,0.66)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: '"EB Garamond", "Garamond", serif',
      }}
      onClick={handleDiscard}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: SURFACE,
          border: `1px solid ${BORDER}`, borderRadius: 6,
          width: 'min(720px, 92vw)',
          height: 'min(720px, 90vh)',
          color: TEXT,
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
        }}
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <div style={{
          padding: '16px 22px', borderBottom: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', gap: 14,
          flexShrink: 0,
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            background: phase === 'recording' ? RED
                      : phase === 'paused'    ? '#F59E0B'
                      : phase === 'results'   ? GREEN
                      : phase === 'parsing'   ? ACCENT
                      : TEXT_FAINT,
            animation: phase === 'recording' ? 'sitInPulse 1.4s infinite' : 'none',
          }} />
          <div style={{
            fontFamily: 'Fraunces, serif', fontSize: 18,
            letterSpacing: '0.06em', color: ACCENT,
          }}>
            COAST · SIT-IN LISTENER
          </div>
          <div style={{
            fontSize: 11, color: TEXT_DIM,
            fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em',
          }}>
            {phase === 'idle'      && 'Ready'}
            {phase === 'recording' && `Recording · ${fmtElapsed(elapsedSec)}`}
            {phase === 'paused'    && `Paused · ${fmtElapsed(elapsedSec)}`}
            {phase === 'parsing'   && `Reading ${fmtElapsed(elapsedSec)} of conversation…`}
            {phase === 'results'   && `Done · ${fmtElapsed(elapsedSec)}`}
            {phase === 'error'     && 'Error'}
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={handleDiscard} style={{
            background: 'transparent', border: 'none', color: TEXT_DIM,
            fontSize: 20, cursor: 'pointer', padding: 0, lineHeight: 1,
          }}>×</button>
        </div>

        {/* ── Body ────────────────────────────────────────────────── */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '20px 22px',
        }}>
          {phase === 'idle' && <IdleView onStart={beginRecording} hasSR={!!SR_CTOR} />}
          {(phase === 'recording' || phase === 'paused') && (
            <RecordingView transcript={transcript} interim={interim} phase={phase}
                            bars={bars} silenceWarning={silenceWarning} />
          )}
          {phase === 'parsing' && <ParsingView />}
          {phase === 'results' && parsed && (
            <ResultsView
              parsed={parsed}
              transcript={transcript}
              // When the user clicks an address chip in the results view
              // we auto-save the meeting before navigating to /map. That
              // way the meeting isn't lost if they don't explicitly hit
              // "Save to Board" first.
              onBeforeNavigate={() => { handleSave(); }}
            />
          )}
          {phase === 'error' && <ErrorView error={error} />}
        </div>

        {/* ── Footer controls ──────────────────────────────────────── */}
        <div style={{
          padding: '14px 22px', borderTop: `1px solid ${BORDER}`,
          display: 'flex', alignItems: 'center', gap: 10,
          flexShrink: 0, background: SURFACE,
        }}>
          {phase === 'recording' && (
            <>
              <button onClick={pauseRecording} style={btnSecondary}>Pause</button>
              <button onClick={stopAndParse} style={btnPrimary}>Stop &amp; analyse</button>
              <span style={{ flex: 1 }} />
              <div style={{ color: TEXT_FAINT, fontSize: 11 }}>
                Tab must stay open — browser throttles backgrounded mics.
              </div>
            </>
          )}
          {phase === 'paused' && (
            <>
              <button onClick={resumeRecording} style={btnPrimary}>Resume</button>
              <button onClick={stopAndParse} style={btnSecondary}>Stop &amp; analyse</button>
              <span style={{ flex: 1 }} />
              <button onClick={handleDiscard} style={btnGhost}>Discard</button>
            </>
          )}
          {phase === 'parsing' && (
            <div style={{ color: TEXT_DIM, fontSize: 12 }}>
              This can take 30-90 seconds for long meetings.
            </div>
          )}
          {phase === 'results' && (
            <>
              <button onClick={handleSave}     style={btnPrimary}>Save to Board</button>
              <button onClick={handleDiscard}  style={btnGhost}>Discard</button>
            </>
          )}
          {phase === 'error' && (
            <button onClick={() => { setPhase('idle'); setError(null); }}
              style={btnPrimary}>Try again</button>
          )}
        </div>

        <style>{`
          @keyframes sitInPulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50%      { opacity: 0.55; transform: scale(1.25); }
          }
        `}</style>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────────
 *                              SUB-VIEWS
 * ──────────────────────────────────────────────────────────────────── */

function IdleView({ onStart, hasSR }: { onStart: () => void; hasSR: boolean }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 22, textAlign: 'center',
    }}>
      <div style={{
        fontFamily: 'Fraunces, serif', fontSize: 28,
        color: TEXT, letterSpacing: '0.02em',
      }}>
        Sit in on a conversation.
      </div>
      <div style={{
        fontSize: 14, color: TEXT_DIM, fontStyle: 'italic',
        maxWidth: 460, lineHeight: 1.55,
      }}>
        Coast will listen for up to 40 minutes, transcribe live, and afterwards
        return topics, decisions, action items, and speaker guesses — strictly
        grounded in what was actually said.
      </div>

      <button
        onClick={onStart}
        disabled={!hasSR}
        style={{
          background: ACCENT, color: '#0a1322',
          border: 'none', borderRadius: '50%',
          width: 96, height: 96, fontSize: 14,
          cursor: hasSR ? 'pointer' : 'not-allowed',
          opacity: hasSR ? 1 : 0.4,
          fontFamily: 'inherit', fontWeight: 600,
          letterSpacing: '0.04em', textTransform: 'uppercase',
          boxShadow: hasSR ? `0 0 0 6px rgba(201,169,110,0.18)` : 'none',
          transition: 'transform .15s',
        }}
        onMouseDown={(e) => { (e.currentTarget as HTMLElement).style.transform = 'scale(0.96)'; }}
        onMouseUp={(e)   => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
      >
        Record
      </button>

      <div style={{ fontSize: 11, color: TEXT_FAINT, letterSpacing: '0.08em',
                    textTransform: 'uppercase', marginTop: 10 }}>
        Phase 1 · AI-guessed speakers · no audio leaves your browser
      </div>
      {!hasSR && (
        <div style={{ fontSize: 12, color: '#CC6E5E', maxWidth: 420 }}>
          This browser doesn't support live speech recognition.
          Use Chrome or Safari (macOS).
        </div>
      )}
    </div>
  );
}

function RecordingView({ transcript, interim, phase, bars, silenceWarning }: {
  transcript:    string;
  interim:       string;
  phase:         Phase;
  bars:          number[];
  silenceWarning: boolean;
}) {
  // Smarter auto-scroll (item 7): only auto-scroll if the user is at
  // (or near) the bottom of the transcript pane. If they've scrolled
  // up to re-read, leave them be. The "near-bottom" threshold (32px)
  // is large enough to tolerate a few px of overscroll without
  // becoming sticky.
  const ref = useRef<HTMLDivElement>(null);
  const stickyRef = useRef(true);
  const onScroll = () => {
    const el = ref.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    stickyRef.current = distFromBottom < 32;
  };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (stickyRef.current) el.scrollTop = el.scrollHeight;
  }, [transcript, interim]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      {/* Waveform bar (item 5) */}
      <div style={{
        display: 'flex', alignItems: 'flex-end',
        gap: 3, height: 44, padding: '4px 6px',
        background: SURFACE, border: `1px solid ${BORDER}`,
        borderRadius: 4, flexShrink: 0,
      }}>
        {bars.map((h, i) => (
          <div key={i}
            style={{
              flex: 1,
              height: `${h}px`,
              background: phase === 'recording' ? ACCENT : TEXT_FAINT,
              opacity: phase === 'recording' ? 0.85 : 0.35,
              borderRadius: 1,
              transition: 'height 80ms linear, opacity 200ms',
            }}
          />
        ))}
      </div>

      <div style={{
        fontSize: 10, color: TEXT_DIM, letterSpacing: '0.16em',
        textTransform: 'uppercase', flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span>Live transcript {phase === 'paused' ? '· paused' : ''}</span>
        {silenceWarning && phase === 'recording' && (
          <span style={{
            color: '#B25A00', background: 'rgba(202, 138, 4, 0.10)',
            border: '1px solid rgba(202, 138, 4, 0.30)',
            padding: '2px 8px', borderRadius: 99,
            fontSize: 9, textTransform: 'none', letterSpacing: '0.02em',
            fontStyle: 'italic', fontWeight: 600,
          }}>
            ⚠ No new transcript in 60s — Speech may have stalled. Consider Stop & analyse.
          </span>
        )}
      </div>
      <div
        ref={ref}
        onScroll={onScroll}
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          background: BG, border: `1px solid ${BORDER}`, borderRadius: 4,
          padding: 16, fontSize: 14, lineHeight: 1.6,
          color: TEXT, fontStyle: 'italic',
        }}
      >
        {transcript || (
          <span style={{ color: TEXT_FAINT, fontStyle: 'italic' }}>
            Listening…
          </span>
        )}
        {interim && (
          <span style={{ color: TEXT_DIM, opacity: 0.75 }}> {interim}</span>
        )}
      </div>
    </div>
  );
}

function ParsingView() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 14,
    }}>
      <div style={{
        width: 36, height: 36, border: `3px solid ${BORDER}`,
        borderTopColor: ACCENT, borderRadius: '50%',
        animation: 'sitInSpin 0.9s linear infinite',
      }} />
      <div style={{ fontSize: 13, color: TEXT_DIM, fontStyle: 'italic', maxWidth: 380, textAlign: 'center', lineHeight: 1.55 }}>
        Reading the conversation. Identifying speakers, topics, decisions,
        action items, and entities. This stays grounded — nothing fabricated.
      </div>
      <style>{`
        @keyframes sitInSpin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function ResultsView({ parsed, transcript, onBeforeNavigate }: {
  parsed: ParsedMeeting; transcript: string;
  // Called before any address-chip navigation. Lets the parent
  // auto-save the meeting so chip-clicks-without-Save don't lose data.
  onBeforeNavigate?: () => void;
}) {
  const [showTranscript, setShowTranscript] = useState(false);
  const handleAddressClick = (addr: string) => {
    // Auto-save first so the meeting always lands on the Board even
    // when the user skips the explicit "Save to Board" button.
    try { onBeforeNavigate?.(); } catch { /* don't block nav on save errors */ }
    const url = `/map?intel_address=${encodeURIComponent(addr)}`;
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Executive summary */}
      <div>
        <SectionLabel>Summary</SectionLabel>
        <div style={{
          fontFamily: 'Fraunces, serif', fontSize: 18, color: TEXT,
          lineHeight: 1.4, letterSpacing: '0.01em',
        }}>
          {parsed.summary || <span style={{ color: TEXT_DIM, fontStyle: 'italic' }}>—</span>}
        </div>
      </div>

      {/* Speakers */}
      {parsed.speakers && parsed.speakers.length > 0 && (
        <div>
          <SectionLabel right="AI-guessed">Speakers</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {parsed.speakers.map((s) => (
              <div key={s.id} style={{
                background: SURFACE_HI, border: `1px solid ${BORDER}`,
                borderRadius: 4, padding: '8px 12px',
                fontSize: 12, color: TEXT, minWidth: 140,
              }}>
                <div style={{ fontWeight: 600 }}>
                  {s.name || s.id}
                  {s.role_guess && (
                    <span style={{ color: TEXT_DIM, fontWeight: 400 }}> · {s.role_guess}</span>
                  )}
                </div>
                <div style={{ fontSize: 10, color: TEXT_FAINT, marginTop: 2 }}>
                  {Math.round(s.share_pct)}% · confidence {s.confidence}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Topics */}
      {parsed.topics && parsed.topics.length > 0 && (
        <div>
          <SectionLabel>Topics discussed</SectionLabel>
          <ol style={{ margin: 0, paddingLeft: 18, color: TEXT, fontSize: 13, lineHeight: 1.6 }}>
            {parsed.topics.map((t, i) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>{t.label}</span>
                {t.summary && (
                  <span style={{ color: TEXT_DIM }}> — {t.summary}</span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Decisions */}
      {parsed.decisions && parsed.decisions.length > 0 && (
        <div>
          <SectionLabel>Decisions</SectionLabel>
          <ResultList items={parsed.decisions.map(d => ({ primary: d.decision, quote: d.quote }))} />
        </div>
      )}

      {/* Action items */}
      {parsed.action_items && parsed.action_items.length > 0 && (
        <div>
          <SectionLabel>Action items</SectionLabel>
          <ResultList items={parsed.action_items.map(a => ({
            primary: a.action,
            meta: [a.owner && `Owner: ${a.owner}`, a.deadline && `Due: ${a.deadline}`]
                    .filter(Boolean).join(' · '),
            quote: a.quote,
          }))} />
        </div>
      )}

      {/* Open questions */}
      {parsed.open_questions && parsed.open_questions.length > 0 && (
        <div>
          <SectionLabel>Open questions</SectionLabel>
          <ResultList items={parsed.open_questions.map(q => ({ primary: q.question, quote: q.quote }))} />
        </div>
      )}

      {/* Risks */}
      {parsed.risks_flagged && parsed.risks_flagged.length > 0 && (
        <div>
          <SectionLabel>Risks flagged</SectionLabel>
          <ResultList items={parsed.risks_flagged.map(r => ({ primary: r.risk, quote: r.quote }))} />
        </div>
      )}

      {/* Entities */}
      {parsed.entities && (() => {
        const e = parsed.entities;
        const total = e.addresses.length + e.properties.length + e.tribes.length
                    + e.regulations.length + e.people.length + e.money.length;
        if (total === 0) return null;
        return (
          <div>
            <SectionLabel>Mentioned in the meeting</SectionLabel>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {e.addresses.length > 0 && (
                <EntityRowResults label="Address" items={e.addresses}
                  actionable onClick={handleAddressClick} />
              )}
              {e.properties.length > 0 && <EntityRowResults label="Property" items={e.properties} />}
              {e.tribes.length > 0 && <EntityRowResults label="Tribe" items={e.tribes} />}
              {e.regulations.length > 0 && <EntityRowResults label="Reg / case" items={e.regulations} />}
              {e.people.length > 0 && <EntityRowResults label="Person" items={e.people} />}
              {e.money.length > 0 && <EntityRowResults label="$" items={e.money} />}
            </div>
          </div>
        );
      })()}

      {/* Transcript toggle */}
      <div>
        <button onClick={() => setShowTranscript(s => !s)} style={{
          background: 'transparent', border: 'none', color: ACCENT,
          fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', cursor: 'pointer', padding: 0,
          fontFamily: 'inherit',
        }}>
          {showTranscript ? '▼ Hide full transcript' : '▶ Show full transcript'}
        </button>
        {showTranscript && (
          <div style={{
            marginTop: 8,
            background: BG, border: `1px solid ${BORDER}`, borderRadius: 4,
            padding: 14, fontSize: 12, lineHeight: 1.6, color: TEXT_DIM,
            fontStyle: 'italic', maxHeight: 300, overflowY: 'auto',
            whiteSpace: 'pre-wrap',
          }}>
            "{transcript}"
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorView({ error }: { error: string | null }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: '100%', gap: 14, textAlign: 'center',
      padding: 20,
    }}>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, color: RED }}>
        Something went wrong.
      </div>
      <div style={{
        color: TEXT_DIM, fontSize: 13, maxWidth: 480, lineHeight: 1.6,
        whiteSpace: 'pre-wrap', textAlign: 'left',
      }}>
        {error || 'An unknown error occurred.'}
      </div>
    </div>
  );
}

/* ── Helper components ─────────────────────────────────────────────── */
function SectionLabel({ children, right }: { children: React.ReactNode; right?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8,
    }}>
      <div style={{
        fontSize: 9, color: ACCENT, letterSpacing: '0.22em',
        textTransform: 'uppercase', fontWeight: 700,
      }}>
        {children}
      </div>
      {right && (
        <div style={{ fontSize: 9, color: TEXT_FAINT, letterSpacing: '0.08em',
                       fontStyle: 'italic' }}>
          {right}
        </div>
      )}
    </div>
  );
}

function ResultList({ items }: {
  items: Array<{ primary: string; meta?: string; quote?: string }>;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((it, i) => (
        <div key={i} style={{
          padding: '10px 12px', background: SURFACE_HI,
          border: `1px solid ${BORDER}`, borderRadius: 4,
        }}>
          <div style={{ color: TEXT, fontSize: 13, lineHeight: 1.45 }}>
            {it.primary}
          </div>
          {it.meta && (
            <div style={{ color: ACCENT_2, fontSize: 10, marginTop: 4, letterSpacing: '0.04em' }}>
              {it.meta}
            </div>
          )}
          {it.quote && (
            <div style={{
              marginTop: 6, paddingLeft: 8, borderLeft: `2px solid ${BORDER}`,
              color: TEXT_DIM, fontSize: 11, fontStyle: 'italic', lineHeight: 1.55,
            }}>
              "{it.quote}"
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function EntityRowResults({ label, items, actionable, onClick }: {
  label: string;
  items: string[];
  actionable?: boolean;
  onClick?: (item: string) => void;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap',
    }}>
      <div style={{
        flexShrink: 0, fontSize: 9, color: TEXT_DIM,
        fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', minWidth: 70, paddingTop: 3,
      }}>
        {label}
      </div>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {items.map((it, i) => (
          <button
            key={`${label}-${i}`}
            onClick={() => { if (actionable && onClick) onClick(it); }}
            disabled={!actionable}
            style={{
              fontSize: 11, padding: '3px 10px', borderRadius: 999,
              fontFamily: 'inherit',
              border: `1px solid ${actionable ? ACCENT : BORDER}`,
              background: actionable ? 'rgba(201,169,110,0.12)' : 'transparent',
              color: actionable ? ACCENT : TEXT,
              cursor: actionable ? 'pointer' : 'default',
            }}
          >
            {it}{actionable ? ' →' : ''}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Button styles ─────────────────────────────────────────────────── */
const btnPrimary: React.CSSProperties = {
  background: ACCENT, color: '#0a1322', border: 'none',
  padding: '8px 16px', fontSize: 12, fontWeight: 600,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
const btnSecondary: React.CSSProperties = {
  background: 'transparent', color: TEXT,
  border: `1px solid ${ACCENT}`,
  padding: '8px 16px', fontSize: 12, fontWeight: 600,
  letterSpacing: '0.06em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
const btnGhost: React.CSSProperties = {
  background: 'transparent', color: TEXT_DIM,
  border: `1px solid ${BORDER}`,
  padding: '8px 14px', fontSize: 11,
  letterSpacing: '0.04em', textTransform: 'uppercase',
  cursor: 'pointer', borderRadius: 3, fontFamily: 'inherit',
};
